import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Calendar, Search, Check } from 'lucide-react';
import { useEventSearch, mergeEventMatches } from '../../lib/queries/events';
import { useLatestRef } from '../../lib/hooks';

// The ONE quality event picker for form fields & "change event" affordances across the
// app — replaces every bare <select><option>…. Searchable, rich rows (name + date/venue/
// organizer), keyboard-free but tidy. Takes a provided `events` list (each surface keeps its
// own curated/eligible set for the DEFAULT view) with a value/onChange string API. Typing
// searches the BACKEND so any event — past/draft/deep — is reachable (clean default, universal
// access); a custom `onSearch` overrides, `serverSearch={false}` disables. Empty/landing →
// use the full EventPickerCard instead.
export const EventSelect = ({
  events = [], value = '', onChange, placeholder = 'Select event…', className = '', disabled = false, align = 'left', onSearch, serverSearch = true,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [remote, setRemote] = useState(null);   // results from a custom onSearch handler
  const [picked, setPicked] = useState(null);   // remember the chosen object so its label persists even
                                                // when the event isn't in the curated `events` list
  const ref = useRef(null);
  const onSearchRef = useLatestRef(onSearch);
  // Built-in backend search when no custom handler is supplied — every EventSelect reaches any event.
  const useInternal = serverSearch && !onSearch;
  const internal = useEventSearch({ query: q, enabled: useInternal });
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ(''); } };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [open]);

  useEffect(() => {
    if (!onSearchRef.current) return undefined;
    const term = q.trim();
    if (!term) { setRemote(null); return undefined; }
    const ac = new AbortController();
    const t = setTimeout(() => { Promise.resolve(onSearchRef.current(term, ac.signal)).then((rows) => { if (!ac.signal.aborted) setRemote(Array.isArray(rows) ? rows : []); }).catch(() => {}); }, 300);
    return () => { clearTimeout(t); ac.abort(); };
  }, [q]);

  const term = q.trim();
  const nameOf = (e) => e.name || e.title || 'Untitled';
  // Backend results: a custom onSearch's `remote`, or the built-in search (2+ chars). Null when not searching.
  const remoteRows = onSearch ? remote : (useInternal && term.length >= 2 ? ((internal.data?.rows || []).filter((e) => e.backendBacked !== false)) : null);
  // Resolve the selected label from the remembered pick, the curated list, or backend results — so a
  // searched-and-selected event still shows its name instead of falling back to the placeholder.
  const selected = (picked && picked.id === value ? picked : null)
    || events.find((e) => e.id === value)
    || (remoteRows || []).find((e) => e.id === value)
    || null;
  // INSTANT local filter (never blanks) + AUGMENT with backend matches (past/deep), deduped.
  const local = events.filter((e) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return nameOf(e).toLowerCase().includes(s) || String(e.id || '').toLowerCase().includes(s) || String(e.venue || '').toLowerCase().includes(s);
  });
  const matches = mergeEventMatches(local, remoteRows, q);
  const searchingNow = useInternal && term.length >= 2 && internal.isLoading;

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        className={`h-9 w-full inline-flex items-center gap-2 px-3 rounded-md border text-[12.5px] transition-colors ${selected ? 'bg-white border-[#D8D5CF] text-[#1F2937]' : 'bg-white border-[#E6E4E1] text-[#6B7280]'} hover:border-[#C9C6C0] disabled:opacity-50 disabled:pointer-events-none`}>
        <Calendar className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" strokeWidth={1.75} />
        <span className="flex-1 text-left truncate">{selected ? nameOf(selected) : placeholder}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute top-full mt-1 ${align === 'right' ? 'right-0' : 'left-0'} min-w-full w-[300px] bg-white border border-[#E6E4E1] rounded-lg shadow-lg z-50`}>
          <div className="p-2 border-b border-[#EEEDEA]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search events…"
                className="w-full h-8 bg-[#FBFAF7] border border-[#E6E4E1] rounded pl-7 pr-2 text-[12px] placeholder:text-[#9CA3AF] focus:outline-none focus:bg-white focus:border-[#D8D5CF]" />
            </div>
          </div>
          <div className="max-h-[300px] overflow-y-auto py-1">
            {matches.length === 0 && <div className="px-3 py-5 text-center text-[11.5px] text-[#9CA3AF]">{searchingNow ? 'Searching all events…' : `No events match “${q}”.`}</div>}
            {matches.slice(0, 80).map((e) => (
              <button type="button" key={e.id} onClick={() => { setPicked(e); onChange && onChange(e.id, e); setOpen(false); setQ(''); }}
                className={`w-full px-3 py-2 text-left hover:bg-[#FBFAF7] flex items-center gap-2.5 ${e.id === value ? 'bg-[#F2F1ED]' : ''}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium text-[#1F2937] truncate">{nameOf(e)}</div>
                  <div className="text-[10.5px] text-[#9CA3AF] truncate">{[e.date, e.venue, e.organizer].filter(Boolean).join(' · ') || e.slug || e.displayId || e.id}</div>
                </div>
                {e.id === value && <Check className="w-3.5 h-3.5 text-[#1F6A3A] flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
